import React, { useState, useEffect } from "react";
import "./company.css";
import { Table, DatePicker, message } from "antd";
import { useNavigate } from "react-router-dom";
import Instance from "../../AxiosConfig";
import dayjs from "dayjs";
import { Form } from "react-bootstrap";

const DashBoardTable = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const token = localStorage.getItem("token");

  // ✅ Fetch all company data (initial and clear filter)
  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const response = await Instance.get("/compony", {
        params: { page, limit: itemsPerPage },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200 || response.status === 201) {
        const companyData = response.data?.data || response.data || [];
        const formattedData = companyData.map((item, index) => ({
          key: item._id,
          slno: (page - 1) * itemsPerPage + index + 1,
          companyName: item.companyName,
          clientName: item.clientName,
          address: item.address,
          country: item.country,
          city: item.city,
          certificationCount: item.certificationCount,
          status: item.status,
        }));

        setData(formattedData);
        setTotalItems(response.data?.total || companyData.length);
      }
    } catch (error) {
      console.error("Fetch Error:", error.response || error.message);
      message.error("Failed to load company data");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch filtered data
  const handleFilter = async () => {
    setLoading(true);
    try {
      const params = {
        search: search.trim() || "",
        status: status === "All" ? "" : status,
        fromDate: fromDate || "",
        toDate: toDate || "",
        page: currentPage,
        limit: itemsPerPage,
      };

      const response = await Instance.get("/compony/filter", {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200 || response.status === 201) {
        const filteredData = response.data?.data || [];
        if (filteredData.length === 0) {
          message.warning("No companies found for the given filters");
        }

        const formattedData = filteredData.map((item, index) => ({
          key: item._id,
          slno: (currentPage - 1) * itemsPerPage + index + 1,
          companyName: item.companyName,
          clientName: item.clientName,
          address: item.address,
          country: item.country,
          city: item.city,
          certificationCount: item.certificationCount,
          status: item.status,
        }));

        setData(formattedData);
        setTotalItems(response.data?.total || filteredData.length);
      }
    } catch (error) {
      console.error(error.response || error.message);
      message.error("Failed to fetch filtered companies");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Clear filters
  const handleClearFilters = () => {
    setSearch("");
    setStatus("All");
    setFromDate(null);
    setToDate(null);
    fetchData(1);
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage]);

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
  };

  const columns = [
    { title: "SL. No", dataIndex: "slno", key: "slno", width: "8%" },
    { title: "Company Name", dataIndex: "companyName", key: "companyName" },
    { title: "Client Name", dataIndex: "clientName", key: "clientName" },
    { title: "Country", dataIndex: "country", key: "country" },
    { title: "City", dataIndex: "city", key: "city" },
    {
      title: "Certification Count",
      dataIndex: "certificationCount",
      key: "certificationCount",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const lowerStatus = status?.toLowerCase();
        let bgColor = "#FDEAEA",
          textColor = "#FF4D4F";
        if (lowerStatus === "active") {
          bgColor = "#DFF6E4";
          textColor = "#0CBC53";
        } else if (lowerStatus === "inactive") {
          bgColor = "#FFF4E5";
          textColor = "#FFA500";
        }
        return (
          <span
            style={{
              backgroundColor: bgColor,
              color: textColor,
              padding: "4px 10px",
              borderRadius: "12px",
              fontWeight: 500,
              display: "inline-block",
              minWidth: "70px",
              textAlign: "center",
            }}
          >
            {status}
          </span>
        );
      },
    },
  ];

  return (
    <>
       <div className="filter-card p-3">
        <Form>
          <div className="row g-3">
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Search</Form.Label>
                <Form.Control
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                />
              </Form.Group>
            </div>

            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option>All</option>
                  <option>Active</option>
                  <option>Inactive</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-3">
              <Form.Group>
                <Form.Label>From</Form.Label>
                <DatePicker
                  className="form-control"
                  value={fromDate ? dayjs(fromDate) : null}
                  onChange={(date) =>
                    setFromDate(date ? date.format("YYYY-MM-DD") : null)
                  }
                  format="YYYY-MM-DD"
                />
              </Form.Group>
            </div>

            <div className="col-md-3">
              <Form.Group>
                <Form.Label>To</Form.Label>
                <DatePicker
                  className="form-control"
                  value={toDate ? dayjs(toDate) : null}
                  onChange={(date) =>
                    setToDate(date ? date.format("YYYY-MM-DD") : null)
                  }
                  format="YYYY-MM-DD"
                />
              </Form.Group>
            </div>
          </div>
        </Form>

        <div className="d-flex justify-content-end gap-4 my-3">
          <button className="clear-btn-company" onClick={handleClearFilters}>
            CLEAR FILTERS
          </button>
          <button className="clear-btn-company" onClick={handleFilter}>
            SUBMIT
          </button>
        </div>
      </div>
    <div className="dashboard mt-5">
      {/* 🧾 Table Section */}
      <div className="dashboard-table mt-3">
        <h1>Companies List</h1>
        <div className="mt-4">
          <Table
            columns={columns}
            dataSource={data}
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize: itemsPerPage,
              total: totalItems,
              showSizeChanger: false,
              className: "custom-pagination",
            }}
            onChange={handleTableChange}
            rowClassName={() => "custom-row"}
            className="overflow-y-auto"
            onRow={(record) => ({
              onClick: () => navigate(`/company-details/${record.key}`),
            })}
          />
        </div>
      </div>
    </div>
    </>
  );
};

export default DashBoardTable;
